import { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { v4 as uuidv4 } from 'uuid';
import { resolveClientLocale } from 'lib/i18n/browser';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';

import {
  fetchCities,
  searchCities,
  type ProfileCity,
  type ProfileCityInput,
} from './profile.api';

interface CitiesAutocompleteProps {
  accessToken: string | null;
  value: ProfileCityInput[];
  onChange: (cities: ProfileCityInput[]) => void;
  disabled?: boolean;
}

interface PlacePredictionOption {
  placeId: string;
  description: string;
  label: string;
}

interface GooglePlacePrediction {
  description: string;
  place_id: string;
  types?: string[];
}

interface GoogleAutocompleteService {
  getPlacePredictions(
    request: {
      input: string;
      sessionToken?: unknown;
      types?: string[];
    },
    callback: (
      predictions: GooglePlacePrediction[] | null,
      status: string
    ) => void
  ): void;
}

type GoogleMapsWindow = Window & {
  google?: {
    maps?: {
      places?: {
        AutocompleteService?: new () => GoogleAutocompleteService;
        AutocompleteSessionToken?: new () => unknown;
        PlacesServiceStatus?: {
          OK?: string;
        };
      };
    };
  };
};

type AvailabilityState =
  | 'loading'
  | 'ready'
  | 'missing_api_key'
  | 'script_load_failed'
  | 'script_missing_library';

const GOOGLE_MAPS_SCRIPT_ID = 'matcha-google-maps-places-script';
const PLACES_LIBRARY_READY_TIMEOUT_MS = 5000;
const PLACES_LIBRARY_POLL_INTERVAL_MS = 50;
let loadPlacesApiPromise: Promise<void> | null = null;

function normalizeBrowserLocale() {
  return resolveClientLocale();
}

function getMapsWindow(): GoogleMapsWindow | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window as GoogleMapsWindow;
}

function isPlacesLibraryReady(): boolean {
  const mapsWindow = getMapsWindow();
  return Boolean(mapsWindow?.google?.maps?.places?.AutocompleteService);
}

function waitForPlacesLibraryReady(
  timeoutMs: number = PLACES_LIBRARY_READY_TIMEOUT_MS
): Promise<void> {
  if (isPlacesLibraryReady()) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const mapsWindow = getMapsWindow();
    if (!mapsWindow) {
      reject(new Error('Google Places API can only be loaded in the browser.'));
      return;
    }

    const startedAt = Date.now();
    const poll = () => {
      if (isPlacesLibraryReady()) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Google Places library is not available.'));
        return;
      }

      mapsWindow.setTimeout(poll, PLACES_LIBRARY_POLL_INTERVAL_MS);
    };

    poll();
  });
}

function loadGooglePlacesApi(apiKey: string): Promise<void> {
  if (isPlacesLibraryReady()) {
    return Promise.resolve();
  }

  if (loadPlacesApiPromise) {
    return loadPlacesApiPromise;
  }

  loadPlacesApiPromise = new Promise<void>((resolve, reject) => {
    const mapsWindow = getMapsWindow();
    if (!mapsWindow) {
      reject(new Error('Google Places API can only be loaded in the browser.'));
      return;
    }

    const handleLoad = () => {
      waitForPlacesLibraryReady().then(resolve).catch(reject);
    };

    const handleError = () => {
      reject(new Error('Unable to load Google Places script.'));
    };

    const existingScript = document.getElementById(
      GOOGLE_MAPS_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (existingScript) {
      waitForPlacesLibraryReady().then(resolve).catch(reject);
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.id = GOOGLE_MAPS_SCRIPT_ID;
    scriptElement.async = true;
    scriptElement.defer = true;
    scriptElement.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places`;
    scriptElement.addEventListener('load', handleLoad, { once: true });
    scriptElement.addEventListener('error', handleError, { once: true });
    document.head.appendChild(scriptElement);
  });

  loadPlacesApiPromise = loadPlacesApiPromise.catch((error) => {
    loadPlacesApiPromise = null;
    throw error;
  });

  return loadPlacesApiPromise;
}

function toShortLocationLabel(value: string): string {
  const normalizedValue = value.trim().replace(/\s+/g, ' ');
  if (!normalizedValue.length) {
    return '';
  }

  const [firstPart] = normalizedValue.split(',');
  return firstPart?.trim() || normalizedValue;
}

function normalizeLocationLabel(value: string) {
  return toShortLocationLabel(value).slice(0, 256);
}

function normalizeLocationPlaceId(value: string) {
  const normalizedValue = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalizedValue.slice(0, 255);
}

function toOptionFromLocationInput(
  city: ProfileCityInput | ProfileCity
): PlacePredictionOption | null {
  const normalizedPlaceId = city.placeId.trim();
  const normalizedLabel = city.label.trim();
  if (!normalizedPlaceId.length || !normalizedLabel.length) {
    return null;
  }

  return {
    placeId: normalizedPlaceId,
    label: normalizedLabel,
    description: normalizedLabel,
  };
}

function getLocationIdentity(option: PlacePredictionOption) {
  const normalizedPlaceId = option.placeId.trim();
  if (normalizedPlaceId.length) {
    return `place:${normalizedPlaceId}`;
  }

  const normalizedLabel = normalizeLocationLabel(
    option.label || option.description
  ).toLowerCase();
  if (normalizedLabel.length) {
    return `label:${normalizedLabel}`;
  }

  return 'city:unknown';
}

function normalizeLocationOption(option: PlacePredictionOption): PlacePredictionOption | null {
  const normalizedLabel = normalizeLocationLabel(option.label || option.description);
  if (!normalizedLabel.length) {
    return null;
  }

  const normalizedPlaceId =
    option.placeId.trim() ||
    normalizeLocationPlaceId(normalizedLabel) ||
    `manual-${uuidv4().replace(/-/g, '').slice(0, 16)}`;

  return {
    placeId: normalizedPlaceId,
    label: normalizedLabel,
    description: option.description.trim() || normalizedLabel,
  };
}

function mergeUniqueLocationOptions(
  primary: PlacePredictionOption[],
  secondary: PlacePredictionOption[]
) {
  const optionsByIdentity = new Map<string, PlacePredictionOption>();

  for (const option of primary) {
    const normalizedOption = normalizeLocationOption(option);
    if (!normalizedOption) {
      continue;
    }

    const locationIdentity = getLocationIdentity(normalizedOption);
    if (optionsByIdentity.has(locationIdentity)) {
      continue;
    }

    optionsByIdentity.set(locationIdentity, normalizedOption);
  }

  for (const option of secondary) {
    const normalizedOption = normalizeLocationOption(option);
    if (!normalizedOption) {
      continue;
    }

    const locationIdentity = getLocationIdentity(normalizedOption);
    if (optionsByIdentity.has(locationIdentity)) {
      continue;
    }

    optionsByIdentity.set(locationIdentity, normalizedOption);
  }

  return Array.from(optionsByIdentity.values());
}

function resolveTypedLocation(
  rawValue: string,
  locationsPool: PlacePredictionOption[]
): PlacePredictionOption | null {
  const normalizedLabel = normalizeLocationLabel(rawValue);
  if (!normalizedLabel.length) {
    return null;
  }

  const normalizedLabelLowerCase = normalizedLabel.toLowerCase();
  const normalizedPlaceId = normalizeLocationPlaceId(normalizedLabel);

  const matchedLocation = locationsPool.find((city) => {
    const locationLabel = normalizeLocationLabel(
      city.label || city.description
    ).toLowerCase();
    const locationPlaceId = normalizeLocationPlaceId(city.placeId);

    return (
      locationLabel === normalizedLabelLowerCase ||
      (normalizedPlaceId.length > 0 && locationPlaceId === normalizedPlaceId)
    );
  });

  if (matchedLocation) {
    return normalizeLocationOption(matchedLocation);
  }

  const generatedPlaceId =
    normalizedPlaceId || `manual-${uuidv4().replace(/-/g, '').slice(0, 16)}`;

  return {
    placeId: generatedPlaceId,
    label: normalizedLabel,
    description: normalizedLabel,
  };
}

function normalizeSelectedLocations(
  selectedLocations: Array<PlacePredictionOption | string>,
  locationsPool: PlacePredictionOption[]
): ProfileCityInput[] {
  const locationsByIdentity = new Map<string, ProfileCityInput>();

  for (const selectedLocation of selectedLocations) {
    const normalizedLocation =
      typeof selectedLocation === 'string'
        ? resolveTypedLocation(selectedLocation, locationsPool)
        : normalizeLocationOption(selectedLocation);
    if (!normalizedLocation) {
      continue;
    }

    const normalizedPlaceId = normalizedLocation.placeId.trim();
    const normalizedLabel = normalizeLocationLabel(
      normalizedLocation.label || normalizedLocation.description
    );
    if (!normalizedPlaceId.length || !normalizedLabel.length) {
      continue;
    }

    const locationIdentity = `${normalizedPlaceId.toLowerCase()}::${normalizedLabel.toLowerCase()}`;
    if (locationsByIdentity.has(locationIdentity)) {
      continue;
    }

    locationsByIdentity.set(locationIdentity, {
      placeId: normalizedPlaceId,
      label: normalizedLabel,
    });
  }

  return Array.from(locationsByIdentity.values());
}

function resolveHelperText(
  availabilityState: AvailabilityState,
  translations: ReturnType<typeof getTranslations>['adminInputs']
): string {
  if (availabilityState === 'missing_api_key') {
    return translations.cityHelperNoApi;
  }

  if (
    availabilityState === 'script_load_failed' ||
    availabilityState === 'script_missing_library'
  ) {
    return translations.cityHelperUnavailable;
  }

  return translations.cityHelperDefault;
}

function isGooglePlacesStatusOk(status: string): boolean {
  const statusOkValue =
    getMapsWindow()?.google?.maps?.places?.PlacesServiceStatus?.OK ?? 'OK';
  return status === statusOkValue || status === 'OK';
}

function isGooglePlacesRequestDenied(status: string): boolean {
  return status.trim().toUpperCase() === 'REQUEST_DENIED';
}

function queryGoogleCityPredictions(
  autocompleteService: GoogleAutocompleteService,
  sessionToken: unknown,
  query: string
): Promise<{
  options: PlacePredictionOption[];
  wasDenied: boolean;
}> {
  return new Promise((resolve) => {
    const request: {
      input: string;
      sessionToken?: unknown;
      types?: string[];
    } = {
      input: query,
      types: ['(cities)'],
    };

    if (sessionToken) {
      request.sessionToken = sessionToken;
    }

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (!isGooglePlacesStatusOk(status) || !predictions?.length) {
        resolve({
          options: [],
          wasDenied: isGooglePlacesRequestDenied(status),
        });
        return;
      }

      resolve({
        options: predictions
          .filter((prediction) => prediction.types?.includes('locality'))
          .map((prediction) => ({
            placeId: prediction.place_id,
            description: prediction.description,
            label: toShortLocationLabel(prediction.description),
          })),
        wasDenied: false,
      });
    });
  });
}

export function CitiesAutocomplete({
  accessToken,
  value,
  onChange,
  disabled = false,
}: CitiesAutocompleteProps) {
  const locale = useLocale();
  const t = getTranslations(locale).adminInputs;
  const apiKey = useMemo(
    () =>
      process.env.REACT_APP_WEB_GOOGLE_API_KEY?.trim() ||
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY?.trim() ||
      '',
    []
  );
  const browserLocale = useMemo(() => normalizeBrowserLocale(), []);
  const [availabilityState, setAvailabilityState] = useState<AvailabilityState>(
    apiKey.length ? 'loading' : 'missing_api_key'
  );
  const [query, setQuery] = useState('');
  const [catalogOptions, setCatalogOptions] = useState<PlacePredictionOption[]>([]);
  const [googleOptions, setGoogleOptions] = useState<PlacePredictionOption[]>([]);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isCatalogBootstrapLoading, setIsCatalogBootstrapLoading] = useState(false);
  const [isCatalogSearchLoading, setIsCatalogSearchLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const autocompleteServiceRef = useRef<GoogleAutocompleteService | null>(null);
  const sessionTokenRef = useRef<unknown>(null);

  const selectedOptions = useMemo(
    () =>
      value
        .map((city) => toOptionFromLocationInput(city))
        .filter((option): option is PlacePredictionOption => option !== null),
    [value]
  );

  const combinedCatalogOptions = useMemo(
    () => mergeUniqueLocationOptions(catalogOptions, selectedOptions),
    [catalogOptions, selectedOptions]
  );

  const combinedOptionsByPlaceId = useMemo(() => {
    const optionsByPlaceId = new Map<string, PlacePredictionOption>();

    for (const option of [...googleOptions, ...combinedCatalogOptions]) {
      const normalizedOption = normalizeLocationOption(option);
      if (!normalizedOption) {
        continue;
      }

      const normalizedPlaceId = normalizedOption.placeId.trim().toLowerCase();
      if (!normalizedPlaceId.length || optionsByPlaceId.has(normalizedPlaceId)) {
        continue;
      }

      optionsByPlaceId.set(normalizedPlaceId, normalizedOption);
    }

    return optionsByPlaceId;
  }, [googleOptions, combinedCatalogOptions]);

  useEffect(() => {
    if (!value.length || !combinedOptionsByPlaceId.size) {
      return;
    }

    const localizedLocations = value.map((city) => {
      const normalizedPlaceId = city.placeId.trim();
      if (!normalizedPlaceId.length) {
        return city;
      }

      const matchedOption = combinedOptionsByPlaceId.get(normalizedPlaceId.toLowerCase());
      if (!matchedOption) {
        return city;
      }

      const normalizedLabel = normalizeLocationLabel(matchedOption.label || matchedOption.description);
      if (!normalizedLabel.length) {
        return city;
      }

      return {
        placeId: matchedOption.placeId,
        label: normalizedLabel,
      };
    });

    const hasChanges = localizedLocations.some((city, index) => {
      const currentLocation = value[index];
      return (
        city.placeId !== currentLocation.placeId ||
        city.label !== currentLocation.label
      );
    });

    if (hasChanges) {
      onChange(localizedLocations);
    }
  }, [combinedOptionsByPlaceId, onChange, value]);

  useEffect(() => {
    let cancelled = false;
    setIsCatalogBootstrapLoading(true);

    fetchCities(browserLocale, 100, 0, accessToken ?? undefined)
      .then((cities) => {
        if (cancelled) {
          return;
        }

        const normalizedLocations = cities
          .map((city) => toOptionFromLocationInput(city))
          .filter((city): city is PlacePredictionOption => city !== null);
        setCatalogOptions((currentOptions) =>
          mergeUniqueLocationOptions(currentOptions, normalizedLocations)
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogOptions((currentOptions) => currentOptions);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsCatalogBootstrapLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, browserLocale]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    // Show bootstrap catalog suggestions when empty query.
    if (!normalizedQuery.length) {
      setGoogleOptions([]);
      setIsCatalogSearchLoading(false);
      return;
    }

    // When Google autocomplete is ready, prefer it and skip catalog search for typed input
    if (availabilityState === 'ready') {
      setIsCatalogSearchLoading(false);
      return;
    }

    let cancelled = false;
    const timerId = window.setTimeout(() => {
      setIsCatalogSearchLoading(true);

      searchCities(normalizedQuery, browserLocale, accessToken ?? undefined)
        .then((cities) => {
          if (cancelled) {
            return;
          }

          const normalizedLocations = cities
            .map((city) => toOptionFromLocationInput(city))
            .filter((city): city is PlacePredictionOption => city !== null);
          setCatalogOptions((currentOptions) =>
            mergeUniqueLocationOptions(currentOptions, normalizedLocations)
          );
        })
        .catch(() => {
          if (!cancelled) {
            setCatalogOptions((currentOptions) => currentOptions);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsCatalogSearchLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [availabilityState, query, browserLocale, accessToken]);

  useEffect(() => {
    if (!isInputFocused) {
      return;
    }

    if (!query.trim().length && !selectedOptions.length && combinedCatalogOptions.length > 0) {
      setIsOptionsOpen(true);
      return;
    }

    if (query.trim().length && googleOptions.length > 0) {
      setIsOptionsOpen(true);
    }
  }, [combinedCatalogOptions.length, googleOptions.length, isInputFocused, query, selectedOptions.length]);

  const resolvedOptions = useMemo(() => {
    const hasQuery = query.trim().length > 0;
    if (hasQuery) {
      // Prefer Google options when typing; fall back to catalog search results if Google is empty.
      if (googleOptions.length) {
        return googleOptions;
      }
      return combinedCatalogOptions;
    }

    // No query: show catalog/bootstrap suggestions so the user can pick without typing.
    return combinedCatalogOptions;
  }, [query, googleOptions, combinedCatalogOptions]);

  useEffect(() => {
    if (!apiKey.length) {
      setAvailabilityState('missing_api_key');
      return;
    }

    let isCancelled = false;
    setAvailabilityState('loading');

    loadGooglePlacesApi(apiKey)
      .then(() => {
        if (isCancelled) {
          return;
        }

        const mapsWindow = getMapsWindow();
        const placesApi = mapsWindow?.google?.maps?.places;
        if (!placesApi?.AutocompleteService) {
          setAvailabilityState('script_missing_library');
          return;
        }

        if (!autocompleteServiceRef.current) {
          autocompleteServiceRef.current = new placesApi.AutocompleteService();
        }
        if (!sessionTokenRef.current && placesApi.AutocompleteSessionToken) {
          sessionTokenRef.current = new placesApi.AutocompleteSessionToken();
        }

        setAvailabilityState('ready');
      })
      .catch(() => {
        if (!isCancelled) {
          setAvailabilityState('script_load_failed');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery.length || availabilityState !== 'ready') {
      setIsGoogleLoading(false);
      setGoogleOptions([]);
      return;
    }

    const autocompleteService = autocompleteServiceRef.current;
    if (!autocompleteService) {
      setIsGoogleLoading(false);
      setGoogleOptions([]);
      return;
    }

    let cancelled = false;
    const timerId = window.setTimeout(() => {
      setIsGoogleLoading(true);

      queryGoogleCityPredictions(
        autocompleteService,
        sessionTokenRef.current,
        normalizedQuery
      )
        .then(({ options: nextOptions, wasDenied }) => {
          if (cancelled) {
            return;
          }

          if (wasDenied) {
            autocompleteServiceRef.current = null;
            sessionTokenRef.current = null;
            setAvailabilityState('script_load_failed');
            setGoogleOptions([]);
            return;
          }

          const selectedLocationIds = new Set(
            selectedOptions.map((selectedOption) => selectedOption.placeId)
          );
          const filteredOptions = nextOptions.filter(
            (option) => !selectedLocationIds.has(option.placeId)
          );
          setGoogleOptions(filteredOptions);
        })
        .finally(() => {
          if (!cancelled) {
            setIsGoogleLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [availabilityState, query, selectedOptions]);

  const isLoading = isCatalogBootstrapLoading || isCatalogSearchLoading || isGoogleLoading;
  const helperText = resolveHelperText(availabilityState, t);

  return (
    <Autocomplete<PlacePredictionOption, true, false, true>
      multiple
      freeSolo
      forcePopupIcon
      options={resolvedOptions}
      value={selectedOptions}
      disabled={disabled}
      loading={isLoading}
      open={isOptionsOpen && resolvedOptions.length > 0}
      onOpen={() => {
        if (!query.trim().length && !selectedOptions.length) {
          if (combinedCatalogOptions.length > 0) {
            setIsOptionsOpen(true);
          }
          return;
        }

        if (resolvedOptions.length > 0) {
          setIsOptionsOpen(true);
        }
      }}
      onClose={() => {
        setIsOptionsOpen(false);
      }}
      filterOptions={(providedOptions) => providedOptions}
      isOptionEqualToValue={(option, selectedOption) =>
        typeof selectedOption !== 'string' && option.placeId === selectedOption.placeId
      }
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description || option.label
      }
      onInputChange={(_, nextQuery, reason) => {
        if (reason === 'input') {
          setQuery(nextQuery);
          if (!nextQuery.trim().length) {
            setIsOptionsOpen(combinedCatalogOptions.length > 0);
          } else {
            setIsOptionsOpen(true);
          }
        } else if (reason === 'clear' || reason === 'reset') {
          setQuery('');
          setIsOptionsOpen(combinedCatalogOptions.length > 0);
        }
      }}
      onChange={(_, nextLocations) => {
        const normalizedLocations = normalizeSelectedLocations(nextLocations, resolvedOptions);
        onChange(normalizedLocations);

        const normalizedOptions = normalizedLocations.map((city) => ({
          placeId: city.placeId,
          label: city.label,
          description: city.label,
        }));
        setCatalogOptions((currentOptions) =>
          mergeUniqueLocationOptions(currentOptions, normalizedOptions)
        );
        setGoogleOptions([]);
        setIsOptionsOpen(false);

        const tokenConstructor =
          getMapsWindow()?.google?.maps?.places?.AutocompleteSessionToken;
        if (tokenConstructor) {
          sessionTokenRef.current = new tokenConstructor();
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t.searchCityPlaceholder}
          helperText={helperText}
          onFocus={() => {
            setIsInputFocused(true);
            if (!query.trim().length && !selectedOptions.length && combinedCatalogOptions.length > 0) {
              setIsOptionsOpen(true);
            }
          }}
          onBlur={() => {
            setIsInputFocused(false);
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
